import { useEffect, useState } from "react";
import { base44 } from "@/api/base44Client";
import AdminGuard from "@/components/admin/AdminGuard";
import AdminLayout from "@/components/admin/AdminLayout";
import { useApp } from "@/lib/AppContext";

const GRADIENTS = [
  { label: "أزرق غامق", value: "from-[#0f4c81] to-[#2a8ad2]" },
  { label: "أزرق ملكي", value: "from-[#1E5FA8] to-[#0e3060]" },
  { label: "تركوازي", value: "from-[#00a8b5] to-[#00d2e0]" },
  { label: "أخضر", value: "from-[#27AE60] to-[#16A085]" },
  { label: "برتقالي", value: "from-[#F5A623] to-[#E67E22]" },
  { label: "أحمر", value: "from-[#E74C3C] to-[#C0392B]" },
  { label: "بنفسجي", value: "from-[#8E44AD] to-[#6C3483]" },
  { label: "وردي", value: "from-[#EC407A] to-[#C2185B]" },
];

const EMPTY = {
  title: "", title_en: "", subtitle: "", subtitle_en: "",
  badge_text: "", badge_text_en: "",
  button_text: "اطلب الآن", button_text_en: "Order Now",
  button_link: "/laundry", icon: "🎁",
  bg_gradient: GRADIENTS[0].value, is_active: true, sort_order: 0,
};

const T = {
  ar: {
    title: "إدارة بنرات العروض",
    addNew: "إضافة بنر جديد",
    save: "حفظ", cancel: "إلغاء", delete: "حذف",
    titleAr: "العنوان الرئيسي (عربي) *", titleEn: "Main Title (English)",
    subtitleAr: "النص الفرعي (عربي)", subtitleEn: "Subtitle (English)",
    badgeAr: "نص الشارة/الكود (عربي)", badgeEn: "Badge Text (English)",
    btnAr: "نص الزر (عربي)", btnEn: "Button Text (English)",
    btnLink: "رابط الزر", icon: "الأيقونة (إيموجي)",
    gradient: "اللون", order: "الترتيب", active: "نشط",
    empty: "لا توجد بنرات — أضف أول بنر",
    saving: "⏳ جاري الحفظ...",
    preview: "معاينة",
  },
  en: {
    title: "Offer Banners Management",
    addNew: "Add New Banner",
    save: "Save", cancel: "Cancel", delete: "Delete",
    titleAr: "Title (Arabic) *", titleEn: "Title (English)",
    subtitleAr: "Subtitle (Arabic)", subtitleEn: "Subtitle (English)",
    badgeAr: "Badge Text (Arabic)", badgeEn: "Badge Text (English)",
    btnAr: "Button Text (Arabic)", btnEn: "Button Text (English)",
    btnLink: "Button Link", icon: "Icon (Emoji)",
    gradient: "Color", order: "Order", active: "Active",
    empty: "No banners yet — add your first banner",
    saving: "⏳ Saving...",
    preview: "Preview",
  }
};

export default function AdminOfferBanners() {
  const { lang } = useApp();
  const tr = T[lang] || T.ar;
  const isAr = lang === "ar";
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    base44.entities.OfferBanner.list("sort_order", 50)
      .then(data => { setBanners(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...b }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    setSaving(true);
    try {
      if (editing) {
        const updated = await base44.entities.OfferBanner.update(editing.id, form);
        setBanners(prev => prev.map(b => b.id === editing.id ? updated : b));
      } else {
        const created = await base44.entities.OfferBanner.create(form);
        setBanners(prev => [...prev, created]);
      }
      setShowForm(false);
    } catch (err) { /* let bubble */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isAr ? "هل أنت متأكد؟" : "Are you sure?")) return;
    await base44.entities.OfferBanner.delete(id);
    setBanners(prev => prev.filter(b => b.id !== id));
  };

  const toggleActive = async (b) => {
    const updated = await base44.entities.OfferBanner.update(b.id, { is_active: !b.is_active });
    setBanners(prev => prev.map(x => x.id === b.id ? updated : x));
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-5" dir={isAr ? "rtl" : "ltr"}>
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-black text-foreground">{tr.title}</h1>
            <button onClick={openNew}
              className="bg-primary text-primary-foreground font-bold px-4 py-2.5 rounded-xl text-sm hover:opacity-90 transition-all">
              + {tr.addNew}
            </button>
          </div>

          {/* Form */}
          {showForm && (
            <form onSubmit={handleSave} className="bg-card border border-border rounded-2xl p-5 space-y-4">
              {/* Titles */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.titleAr}</label>
                  <input value={form.title} onChange={e => upd("title", e.target.value)}
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" required />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.titleEn}</label>
                  <input value={form.title_en} onChange={e => upd("title_en", e.target.value)} dir="ltr"
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
              </div>

              {/* Subtitles */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.subtitleAr}</label>
                  <input value={form.subtitle} onChange={e => upd("subtitle", e.target.value)}
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.subtitleEn}</label>
                  <input value={form.subtitle_en} onChange={e => upd("subtitle_en", e.target.value)} dir="ltr"
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
              </div>

              {/* Badge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.badgeAr}</label>
                  <input value={form.badge_text} onChange={e => upd("badge_text", e.target.value)}
                    placeholder="استخدم الكود: WELCOME15"
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.badgeEn}</label>
                  <input value={form.badge_text_en} onChange={e => upd("badge_text_en", e.target.value)} dir="ltr"
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
              </div>

              {/* Button text & link */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.btnAr}</label>
                  <input value={form.button_text} onChange={e => upd("button_text", e.target.value)}
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.btnEn}</label>
                  <input value={form.button_text_en} onChange={e => upd("button_text_en", e.target.value)} dir="ltr"
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.btnLink}</label>
                  <input value={form.button_link} onChange={e => upd("button_link", e.target.value)} dir="ltr"
                    placeholder="/laundry"
                    className="w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground" />
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.icon}</label>
                  <div className="flex gap-2">
                    <input value={form.icon} onChange={e => upd("icon", e.target.value)}
                      className="w-20 bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary text-foreground text-center text-2xl" />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {["🎁","🏷️","🚗","💧","🔥","⏱️","🫧","⭐","💰","🧺"].map(em => (
                        <button key={em} type="button" onClick={() => upd("icon", em)}
                          className={`text-2xl p-1.5 rounded-lg transition-all ${form.icon === em ? "bg-primary/20 ring-2 ring-primary" : "hover:bg-secondary"}`}>
                          {em}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Gradient picker */}
              <div>
                <label className="text-xs font-bold text-muted-foreground block mb-2">{tr.gradient}</label>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {GRADIENTS.map(g => (
                    <button key={g.value} type="button" onClick={() => upd("bg_gradient", g.value)}
                      className={`h-12 rounded-xl bg-gradient-to-br ${g.value} transition-all ${form.bg_gradient === g.value ? "ring-2 ring-primary ring-offset-2 scale-105" : ""}`}
                      title={g.label} />
                  ))}
                </div>
              </div>

              {/* Order & active */}
              <div className="flex items-center gap-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground block mb-1">{tr.order}</label>
                  <input type="number" value={form.sort_order} onChange={e => upd("sort_order", Number(e.target.value))}
                    className="w-20 bg-secondary border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-primary text-foreground text-center" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-4">
                  <input type="checkbox" checked={form.is_active} onChange={e => upd("is_active", e.target.checked)}
                    className="w-4 h-4 accent-primary" />
                  <span className="text-sm font-bold text-foreground">{tr.active}</span>
                </label>
              </div>

              {/* Live preview */}
              {form.title && (
                <div>
                  <p className="text-xs font-bold text-muted-foreground mb-2">{tr.preview}</p>
                  <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${form.bg_gradient} p-4 flex items-center min-h-[120px]`}>
                    <div className="absolute top-0 end-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/3" />
                    <div className="relative z-10 flex-1 min-w-0">
                      {form.badge_text && <p className="text-white/80 text-xs font-bold mb-1">{form.badge_text}</p>}
                      <h3 className="text-white font-black text-xl mb-1">{form.title}</h3>
                      {form.subtitle && <p className="text-white/70 text-xs mb-2">{form.subtitle}</p>}
                      <span className="inline-flex items-center gap-1 bg-white text-slate-800 font-bold text-xs px-4 py-1.5 rounded-full">
                        {form.button_text || "اطلب الآن"} 🛒
                      </span>
                    </div>
                    {form.icon && (
                      <div className="relative z-10 ps-3">
                        <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center text-3xl">
                          {form.icon}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={saving}
                  className="bg-primary text-primary-foreground font-bold px-5 py-2.5 rounded-xl text-sm hover:opacity-90 disabled:opacity-60 transition-all">
                  {saving ? tr.saving : tr.save}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="bg-secondary text-muted-foreground font-bold px-5 py-2.5 rounded-xl text-sm hover:opacity-80 transition-all">
                  {tr.cancel}
                </button>
              </div>
            </form>
          )}

          {/* List */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => <div key={i} className="bg-card rounded-2xl p-5 animate-pulse border border-border h-28" />)}
            </div>
          ) : banners.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">{tr.empty}</div>
          ) : (
            <div className="grid gap-3">
              {banners.map(b => (
                <div key={b.id} className={`bg-card border rounded-2xl p-4 flex items-center gap-4 ${b.is_active ? "border-border" : "border-border opacity-60"}`}>
                  {/* Mini preview */}
                  <div className={`w-24 h-16 rounded-xl bg-gradient-to-br ${b.bg_gradient} flex items-center justify-center text-2xl flex-shrink-0`}>
                    {b.icon || "🎁"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-foreground text-sm">{b.title}</p>
                    {b.subtitle && <p className="text-xs text-muted-foreground truncate">{b.subtitle}</p>}
                    {b.badge_text && <p className="text-xs text-primary truncate mt-0.5">{b.badge_text}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => toggleActive(b)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-xl transition-all ${b.is_active ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-secondary text-muted-foreground"}`}>
                      {b.is_active ? (isAr ? "نشط" : "Active") : (isAr ? "غير نشط" : "Inactive")}
                    </button>
                    <button onClick={() => openEdit(b)}
                      className="bg-secondary text-muted-foreground text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all">✏️</button>
                    <button onClick={() => handleDelete(b.id)}
                      className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all">{tr.delete}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}